import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Home, Lock, Loader2, Eye, EyeOff, AlertTriangle, ArrowLeft } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

function PasswordField({ value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-secondary/50 px-3 focus-within:ring-2 focus-within:ring-primary focus-within:bg-background transition-colors">
      <Lock size={16} className="text-muted-foreground" />
      <input
        type={show ? "text" : "password"}
        required
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="flex-1 bg-transparent outline-none text-sm py-3"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Sembunyikan password" : "Tampilkan password"}
        data-testid={`${testid}-toggle`}
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        {show ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </div>
  );
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState({ new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.new_password !== pw.confirm) {
      setError("Konfirmasi password tidak cocok.");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/auth/reset-password", { token, new_password: pw.new_password });
      toast.success("Password berhasil diatur ulang. Silakan login.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 py-16" data-testid="reset-password-page">
        <div className="bg-card border border-border rounded-2xl p-6 text-center space-y-3" data-testid="reset-invalid-token">
          <AlertTriangle size={40} className="mx-auto text-amber-500" />
          <p className="text-sm text-foreground">Tautan reset tidak valid. Silakan minta ulang tautan reset password.</p>
          <Link to="/lupa-password" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline" data-testid="reset-request-again">
            Minta Tautan Baru
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="reset-password-page">
      <div className="text-center mb-8">
        <span className="inline-grid place-items-center w-12 h-12 rounded-2xl bg-primary text-primary-foreground mb-4">
          <Home size={22} />
        </span>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Atur Password Baru</h1>
        <p className="mt-2 text-sm text-muted-foreground">Masukkan password baru untuk akun Anda.</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl p-6 space-y-4">
        {error && (
          <div className="rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive" data-testid="reset-password-error">
            {error}
          </div>
        )}
        <div>
          <label className="text-sm font-medium text-foreground">Password Baru</label>
          <PasswordField
            value={pw.new_password}
            onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
            placeholder="Min. 8 karakter, ada huruf & angka"
            testid="reset-new-password"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-foreground">Ulangi Password Baru</label>
          <PasswordField
            value={pw.confirm}
            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            placeholder="Ketik ulang password baru"
            testid="reset-confirm-password"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="reset-submit-button"
          className="w-full py-3 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          {loading ? "Menyimpan..." : "Simpan Password Baru"}
        </button>
        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline" data-testid="reset-to-login-link">
            <ArrowLeft size={14} /> Kembali ke Login
          </Link>
        </p>
      </form>
    </div>
  );
}
